import { motion } from 'motion/react'
import { Check, ExternalLink } from 'lucide-react'
import { enterVariants } from '../../lib/motion'

function Tile({
  className = '',
  caption,
  children,
}: {
  className?: string
  caption: string
  children?: React.ReactNode
}) {
  return (
    <motion.div
      variants={enterVariants}
      className={`group flex flex-col justify-between gap-6 rounded-2xl border border-white/[0.06] bg-surface-raised p-5 transition-[border-color,box-shadow] duration-200 ease-out hover:border-white/[0.12] hover:shadow-glow-soft ${className}`}
    >
      {children ? <div>{children}</div> : null}
      <p className="text-caption text-white/50">{caption}</p>
    </motion.div>
  )
}

export default function ProductProof() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-28">
      <h2 className="max-w-2xl text-balance text-[1.875rem] font-semibold tracking-[-0.025em] text-white md:text-h2">
        Nothing reaches your board until you say so
      </h2>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        transition={{ staggerChildren: 0.07 }}
        className="mt-12 grid gap-4 md:grid-cols-6"
      >
        <Tile
          className="md:col-span-4 md:row-span-2"
          caption="You review everything first — edit the title, change the owner, drop what you don't need."
        >
          <div className="space-y-2">
            {[
              'Fix checkout currency rounding bug',
              'Update pricing page copy for new tiers',
              'Send onboarding wireframes to Dan',
            ].map((title) => (
              <div
                key={title}
                className="flex items-start gap-2.5 rounded-xl border border-white/[0.06] bg-surface-overlay p-3"
              >
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-primary-400 bg-primary-500">
                  <Check className="h-3 w-3 text-white" aria-hidden="true" />
                </span>
                <span className="text-caption text-white/85">{title}</span>
              </div>
            ))}
          </div>
        </Tile>

        <Tile
          className="md:col-span-2"
          caption="Labels are suggested from the board you already use, not invented."
        >
          <div className="flex flex-wrap gap-1.5">
            {['High', 'Bug', 'Frontend'].map((label) => (
              <span
                key={label}
                className="rounded-md bg-white/[0.06] px-2 py-1 text-caption text-white/70 ring-1 ring-inset ring-white/[0.08]"
              >
                {label}
              </span>
            ))}
          </div>
        </Tile>

        <Tile
          className="md:col-span-2"
          caption="It lands as a normal Trello card — nothing proprietary, nothing to migrate."
        >
          <div className="rounded-xl border border-white/[0.06] bg-surface-overlay p-3">
            <div className="mb-2 h-1.5 w-8 rounded-full bg-red-400/70" />
            <p className="text-caption text-white/85">Fix checkout rounding bug</p>
            <p className="mt-2 inline-flex items-center gap-1 text-caption text-white/50">
              trello.com <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </p>
          </div>
        </Tile>

        <Tile
          className="md:col-span-3"
          caption="Or paste the transcript straight in."
        >
          <p className="text-h3 font-medium text-white">
            .txt <span className="text-white/25">·</span> .md{' '}
            <span className="text-white/25">·</span> .pdf
          </p>
        </Tile>

        <Tile className="md:col-span-3" caption="No upgrade required.">
          <p className="text-h3 font-medium text-white">
            Works with a free Trello account
          </p>
        </Tile>
      </motion.div>
    </section>
  )
}
